// src/services/HapticService.ts

import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { Preferences } from "@capacitor/preferences";

/**
 * HapticService - A utility service for managing haptic feedback
 * in the ARC application
 */
export class HapticService {
  private static instance: HapticService;
  private hapticsEnabled = true;
  private readonly HAPTICS_PREF_KEY = "haptics_enabled";

  private constructor() {
    // Initialize haptics preference
    this.initHapticsPreference();
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): HapticService {
    if (!HapticService.instance) {
      HapticService.instance = new HapticService();
    }
    return HapticService.instance;
  }

  /**
   * Initialize haptics preference from storage
   */
  private async initHapticsPreference(): Promise<void> {
    try {
      const { value } = await Preferences.get({ key: this.HAPTICS_PREF_KEY });
      if (value !== null) {
        this.hapticsEnabled = value === "true";
      }
    } catch (error) {
      console.error("Error loading haptic preferences:", error);
    }
  }

  /**
   * Set whether haptics are enabled
   */
  public async setHapticsEnabled(enabled: boolean): Promise<void> {
    this.hapticsEnabled = enabled;
    try {
      await Preferences.set({
        key: this.HAPTICS_PREF_KEY,
        value: String(enabled),
      });
    } catch (error) {
      console.error("Error saving haptic preferences:", error);
    }
  }

  /**
   * Check if haptics are enabled
   */
  public isHapticsEnabled(): boolean {
    return this.hapticsEnabled;
  }

  /**
   * Trigger impact haptic feedback
   */
  public async impact(style: ImpactStyle = ImpactStyle.Medium): Promise<void> {
    if (!this.hapticsEnabled) return;

    try {
      await Haptics.impact({ style });
    } catch (error) {
      console.error("Error triggering impact haptic:", error);
    }
  }

  /**
   * Trigger notification haptic feedback
   */
  public async notification(
    type: NotificationType = NotificationType.Success
  ): Promise<void> {
    if (!this.hapticsEnabled) return;

    try {
      await Haptics.notification({ type });
    } catch (error) {
      console.error("Error triggering notification haptic:", error);
    }
  }

  /**
   * Trigger vibration
   */
  public async vibrate(duration = 300): Promise<void> {
    if (!this.hapticsEnabled) return;

    try {
      await Haptics.vibrate({ duration });
    } catch (error) {
      console.error("Error triggering vibration:", error);
    }
  }

  /**
   * Trigger selection start haptic feedback
   */
  public async selectionStart(): Promise<void> {
    if (!this.hapticsEnabled) return;

    try {
      await Haptics.selectionStart();
    } catch (error) {
      console.error("Error triggering selection start haptic:", error);
    }
  }

  /**
   * Trigger selection changed haptic feedback
   */
  public async selectionChanged(): Promise<void> {
    if (!this.hapticsEnabled) return;

    try {
      await Haptics.selectionChanged();
    } catch (error) {
      console.error("Error triggering selection changed haptic:", error);
    }
  }

  /**
   * Trigger selection end haptic feedback
   */
  public async selectionEnd(): Promise<void> {
    if (!this.hapticsEnabled) return;

    try {
      await Haptics.selectionEnd();
    } catch (error) {
      console.error("Error triggering selection end haptic:", error);
    }
  }

  /**
   * Convenience methods for common patterns
   */

  /**
   * Success haptic feedback
   */
  public async success(): Promise<void> {
    await this.notification(NotificationType.Success);
  }

  /**
   * Error haptic feedback
   */
  public async error(): Promise<void> {
    await this.notification(NotificationType.Error);
  }

  /**
   * Warning haptic feedback
   */
  public async warning(): Promise<void> {
    await this.notification(NotificationType.Warning);
  }

  /**
   * Light tap haptic feedback
   */
  public async lightTap(): Promise<void> {
    await this.impact(ImpactStyle.Light);
  }

  /**
   * Heavy tap haptic feedback
   */
  public async heavyTap(): Promise<void> {
    await this.impact(ImpactStyle.Heavy);
  }
}

// Export a singleton instance
export const hapticService = HapticService.getInstance();
